"use client";

import { useReactFlow } from "@xyflow/react";
import { useMemo } from "react";
import { getDefinition } from "@/lib/catalog";
import { DEFAULT_SOC } from "@/lib/simulate/battery-runtime";
import { DEFAULT_NOMINAL_V } from "@/lib/voltage-domain";
import { useSystemStore } from "@/lib/store";
import { useCanvasHover } from "@/lib/canvas-hover";
import { useT } from "@/lib/i18n";
import { ControlsSubsectionLabel } from "./controls-subsection-label";
import { focusOnCanvas } from "./focus";

export function BatteriesSection({ showHeading = true, showEnergy = true }: {
  showHeading?: boolean;
  showEnergy?: boolean;
} = {}) {
  const tr = useT();
  const nodes = useSystemStore((s) => s.system.nodes);
  const batterySoc = useSystemStore((s) => s.simulateState.batterySoc);
  const setSoc = useSystemStore((s) => s.setBatterySoc);
  const { fitView } = useReactFlow();
  const setHoverNodeId = useCanvasHover((s) => s.setHoverNodeId);

  const batteries = useMemo(() => {
    return nodes.flatMap((n) => {
      if (n.kind !== "component") return [];
      const def = getDefinition(n.definitionId, n.customDefinition);
      if (!def || def.category !== "battery") return [];
      return [{ node: n, def, label: n.label ?? def.shortName ?? def.name }];
    });
  }, [nodes]);

  if (batteries.length === 0) return null;

  return (
    <div className="px-5 pb-4">
      {showHeading && <ControlsSubsectionLabel className="mb-2">
        {tr("simulate.batteries")}
      </ControlsSubsectionLabel>}
      <div className="space-y-3">
        {batteries.map(({ node, def, label }) => {
          const soc = batterySoc[node.id] ?? DEFAULT_SOC;
          const pct = Math.round(soc * 100);
          const capWh = (def.capacityAh ?? 0) * (def.voltage ?? DEFAULT_NOMINAL_V);
          const usableWh = soc * capWh;
          return (
            <div
              key={node.id}
              onMouseEnter={() => setHoverNodeId(node.id)}
              onMouseLeave={() =>
                setHoverNodeId(
                  useCanvasHover.getState().hoverNodeId === node.id
                    ? null
                    : useCanvasHover.getState().hoverNodeId,
                )
              }
            >
              <div className="flex items-baseline gap-2 mb-1">
                <button
                  type="button"
                  onClick={() => focusOnCanvas([node.id], fitView)}
                  className="flex-1 min-w-0 text-left text-[11.5px] text-[var(--color-fg-muted)] truncate hover:text-[var(--color-fg)]"
                  title={tr("simulate.show_on_canvas")}
                >
                  {label}
                </button>
                <span className="flex-none inline-flex items-baseline gap-0.5 font-mono tabular-nums">
                  <span className="text-[12.5px] font-medium">{pct}</span>
                  <span className="text-[10px] text-[var(--color-fg-subtle)] tracking-wide">
                    %
                  </span>
                </span>
                {showEnergy && <span className="flex-none w-14 text-right font-mono text-[10.5px] tabular-nums text-[var(--color-fg-subtle)]">
                  {Math.round(usableWh)} Wh
                </span>}
              </div>
              <input
                type="range"
                aria-label={`${label} charge`}
                aria-valuetext={`${pct}%`}
                min={0}
                max={100}
                step={1}
                value={pct}
                onChange={(e) => setSoc(node.id, Number(e.target.value) / 100)}
                className="scenario-slider w-full h-2 cursor-pointer appearance-none bg-transparent"
                style={{
                  background: `linear-gradient(to right, var(--color-fg) 0%, var(--color-fg) ${pct}%, var(--color-border) ${pct}%, var(--color-border) 100%)`,
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
